import {
  Alert,
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import {
  Article,
  CalculationType,
  DeviceDataGroup,
  ExaminationConfig,
  Laboratory,
  Skill,
  TaskConfig,
  TreoLocation,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { SyntheticEvent, useEffect, useState } from "react"
import { treoLocationList } from "utils/constants"
import { AddCalculation } from "./AddCalculation"

type AddExamConfigDialogProps = {
  open: boolean
  onClose: () => void
  formState: ExaminationConfig
  onSubmit: (arg: ExaminationConfig) => void
}

type ValidationErrors = {
  name?: string
  eutDataGroups?: string
  tasks?: string
  calculationType?: string
}

export const AddExamConfigDialog = (props: AddExamConfigDialogProps) => {
  const { open, onClose, formState, onSubmit } = props
  const [eutDataGroupCalc, setEutDataGroupCalc] = useState<DeviceDataGroup[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const {
    skills,
    deviceDataGroups,
    taskConfigs,

    laboratories,
    articles,
    examinationConfigs,
  } = useStoreState((state) => state)
  const {
    fetchSkills,
    fetchDeviceDataGroups,
    fetchTaskConfigs,
    fetchLaboratories,
    fetchArticles,
    fetchExaminationConfigs,
  } = useStoreActions((actions) => actions)

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchSkills({ fresh: false }),
          fetchDeviceDataGroups({ fresh: false }),
          fetchTaskConfigs({ fresh: false }),
          fetchLaboratories({ fresh: false }),
          fetchArticles({ fresh: false }),
          fetchExaminationConfigs({ fresh: false }),
        ])
      } catch (err) {
        console.error("Error loading data:", err)
        // Individual fetch functions now handle their own errors gracefully
      }
    }

    loadData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [localFormState, setLocalFormState] = useState<ExaminationConfig>(formState)

  useEffect(() => {
    setLocalFormState(formState)

    // Initialize eutDataGroupCalc based on the form state's EUT data groups
    if (formState.eutDataGroups?.length > 0) {
      const newEutDataGroupCalc = formState.eutDataGroups.filter((group) =>
        group.deviceData.some((data) => data.isCalculation)
      )
      setEutDataGroupCalc(newEutDataGroupCalc)
    } else {
      setEutDataGroupCalc([])
    }
  }, [formState])

  // Keep eutDataGroupCalc in sync with localFormState.eutDataGroups
  useEffect(() => {
    if (localFormState.eutDataGroups?.length > 0) {
      const newEutDataGroupCalc = localFormState.eutDataGroups.filter((group) =>
        group.deviceData.some((data) => data.isCalculation)
      )
      setEutDataGroupCalc(newEutDataGroupCalc)
    } else {
      setEutDataGroupCalc([])
    }
  }, [localFormState.eutDataGroups])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Bezeichnung ist erforderlich"
    } else {
      // Check for unique name (excluding current item being edited)
      const existingConfig = examinationConfigs.find(
        (config) =>
          config.name.toLowerCase() === localFormState.name.toLowerCase() &&
          config.id !== localFormState.id
      )
      if (existingConfig) {
        errors.name = "Diese Bezeichnung ist bereits vergeben"
      }
    }

    if (!localFormState.eutDataGroups?.length) {
      errors.eutDataGroups = "EUT Eigenschaften sind erforderlich"
    }

    if (!localFormState.tasks?.length) {
      errors.tasks = "Arbeitsgang ist erforderlich"
    }

    if (!localFormState.defaultCalculation?.calculationType) {
      errors.calculationType = "Kalkulationsart ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      setLocalFormState((prevState) => ({ ...prevState, [name]: value }))
      // Clear validation error for this field
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name as keyof ValidationErrors]
          return newErrors
        })
      }
    }
  }

  const handleFormChangeLaboratory = (
    _: SyntheticEvent<Element, Event>,
    value: Laboratory | null
  ) => {
    if (value)
      setLocalFormState((prevState) => ({
        ...prevState,
        laboratory: value,
      }))
  }

  const handleFormChangeLocation = (
    _: SyntheticEvent<Element, Event>,
    value: TreoLocation | null
  ) => {
    if (value)
      setLocalFormState((prevState) => ({
        ...prevState,
        location: value,
      }))
  }

  const handleFormChangeSkill = (_: SyntheticEvent<Element, Event>, value: Skill | null) => {
    if (value)
      setLocalFormState((prevState) => ({
        ...prevState,
        skill: value,
      }))
  }

  const handleFormChangeArticle = (_: SyntheticEvent<Element, Event>, value: Article | null) => {
    if (value)
      setLocalFormState((prevState) => ({
        ...prevState,
        article: value.inventoryID || "",
      }))
  }

  const handleFormChangeEutDataGroups = (
    _: SyntheticEvent<Element, Event>,
    value: DeviceDataGroup[]
  ) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      eutDataGroups: value,
    }))

    // Clear validation error for this field
    if (validationErrors.eutDataGroups) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.eutDataGroups
        return newErrors
      })
    }

    // Note: eutDataGroupCalc is now updated automatically by useEffect watching localFormState.eutDataGroups
  }

  const handleFormChangeTasks = (_: SyntheticEvent<Element, Event>, value: TaskConfig[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      tasks: value,
    }))

    // Clear validation error for this field
    if (validationErrors.tasks) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.tasks
        return newErrors
      })
    }
  }

  const handleFormChangeCalculationType = (
    _: SyntheticEvent<Element, Event>,
    value: CalculationType | null
  ) => {
    if (value)
      setLocalFormState((prevState) => ({
        ...prevState,
        defaultCalculation: {
          ...prevState.defaultCalculation,
          calculationType: value,
        },
      }))

    // Clear validation error for this field
    if (validationErrors.calculationType) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.calculationType
        return newErrors
      })
    }

    if (eutDataGroupCalc.length > 0 && value === CalculationType.CONNECTION) {
      const newConnections = eutDataGroupCalc.flatMap((i) => i.deviceData)
      const newCalConnetions = newConnections.filter((i) => i.isCalculation)
      const newConnectionPrice = newCalConnetions.map((group) => {
        return {
          name: group.name,
          price: 0,
          cost: 0,
          amount: 0,
        }
      })
      setLocalFormState((prevState) => ({
        ...prevState,
        defaultCalculation: {
          ...prevState.defaultCalculation,
          connectionPrice: newConnectionPrice,
        },
      }))
    }
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <Dialog open={open} maxWidth="xl" fullWidth>
      <DialogContent sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Prüfverfahren</Typography>

          {hasValidationErrors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                Bitte korrigieren Sie die folgenden Fehler:
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(validationErrors).map(([field, error]) => (
                  <Typography key={field} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <TextField
            size="small"
            name="name"
            label="Bezeichnung"
            type="text"
            fullWidth
            value={localFormState.name}
            onChange={handleFormChange}
            required
            error={!!validationErrors.name}
            helperText={validationErrors.name}
          />

          <Autocomplete
            size="small"
            options={laboratories}
            value={localFormState.laboratory}
            onChange={handleFormChangeLaboratory}
            renderInput={(params) => <TextField {...params} label="Laborbereich" />}
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(option) => option.name}
          />

          <Autocomplete
            size="small"
            options={treoLocationList}
            value={localFormState.location}
            onChange={handleFormChangeLocation}
            renderInput={(params) => <TextField {...params} label="Standort" />}
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(option) => option.name}
          />

          <Autocomplete
            size="small"
            options={skills}
            value={localFormState.skill}
            onChange={handleFormChangeSkill}
            renderInput={(params) => <TextField {...params} label="Skill" />}
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(option) => option.name}
          />

          <AutocompleteMulti
            options={deviceDataGroups}
            value={localFormState.eutDataGroups}
            onChange={handleFormChangeEutDataGroups}
            label="EUT Eigenschaften"
            required
            error={!!validationErrors.eutDataGroups}
            helperText={validationErrors.eutDataGroups}
          />

          <AutocompleteMulti
            options={taskConfigs}
            value={localFormState.tasks}
            onChange={handleFormChangeTasks}
            label="Arbeitsgang"
            required
            error={!!validationErrors.tasks}
            helperText={validationErrors.tasks}
          />

          <Autocomplete
            size="small"
            options={articles}
            value={articles.find((article) => article.inventoryID === localFormState.article)}
            onChange={handleFormChangeArticle}
            renderInput={(params) => <TextField {...params} label="Artikel" />}
            renderOption={(props, option) => (
              <li {...props}>
                {/* {option.description} {option.inventoryID} */}
                {`${option.description} {${option.inventoryID}}`}
              </li>
            )}
            getOptionLabel={(option) => `${option.description} {${option.inventoryID}}`}
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Standardkalkulation für das Prüfverfahren</Typography>
          <Autocomplete
            size="small"
            options={
              eutDataGroupCalc.length > 0 ? Object.values(CalculationType) : [CalculationType.TIME]
            }
            value={localFormState.defaultCalculation.calculationType}
            onChange={handleFormChangeCalculationType}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Kalkulationsart"
                required
                error={!!validationErrors.calculationType}
                helperText={validationErrors.calculationType}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                {option}
              </li>
            )}
          />

          <AddCalculation
            formState={localFormState.defaultCalculation}
            setFormState={(value) => {
              setLocalFormState((prevState) => ({
                ...prevState,
                defaultCalculation: value,
              }))
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} variant="contained" />
        <StyledButton
          buttonType="save"
          onClick={handleSave}
          disabled={hasValidationErrors}
          variant="contained"
        />
      </DialogActions>
    </Dialog>
  )
}
